import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, Inbox, Flame, MessagesSquare, ArrowLeft, CreditCard, Link2, Copy, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const fmtMoney = (pence, currency = "gbp") => `£${((pence || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  paid: "text-emerald-400 bg-emerald-400/10 border-emerald-400/25",
  pending: "text-amber-400 bg-amber-400/10 border-amber-400/25",
  failed: "text-red-400 bg-red-400/10 border-red-400/25",
  expired: "text-neutral-400 bg-white/5 border-white/10",
  refunded: "text-slate-400 bg-slate-400/10 border-slate-400/25",
  cancelled: "text-orange-400 bg-orange-400/10 border-orange-400/25",
  cancelling: "text-amber-300 bg-amber-300/10 border-amber-300/25",
};

const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-[#0055FF] transition-colors duration-200";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ws_admin_token") || "");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [chats, setChats] = useState([]);
  const [payments, setPayments] = useState([]);
  const [openChat, setOpenChat] = useState(null);
  const [depAmount, setDepAmount] = useState("");
  const [depDesc, setDepDesc] = useState("");
  const [depClient, setDepClient] = useState("");
  const [depLink, setDepLink] = useState("");
  const [depBusy, setDepBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (t) => {
    try {
      const headers = { Authorization: `Bearer ${t}` };
      const [e, c, p] = await Promise.all([
        axios.get(`${API}/enquiries`, { headers }),
        axios.get(`${API}/ai-chats`, { headers }),
        axios.get(`${API}/payments`, { headers }),
      ]);
      setEnquiries(e.data);
      setChats(c.data);
      setPayments(p.data);
    } catch {
      setToken("");
      localStorage.removeItem("ws_admin_token");
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem("ws_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => { localStorage.removeItem("ws_admin_token"); setToken(""); };

  const generateDeposit = async (e) => {
    e.preventDefault();
    if (depBusy || !depAmount || !depDesc.trim()) return;
    setDepBusy(true);
    setDepLink("");
    try {
      const { data } = await axios.post(`${API}/payments/deposit-link`,
        { amount: parseFloat(depAmount), description: depDesc, client_name: depClient || undefined },
        { headers: { Authorization: `Bearer ${token}` } });
      setDepLink(data.url);
      load(token);
      toast.success("Deposit link created.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the link. Try again.");
    } finally {
      setDepBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(depLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the link manually.");
    }
  };

  const waShare = () => {
    const text = `Hi${depClient ? " " + depClient : ""}, here's the secure payment link for your deposit (${depDesc} — £${depAmount}): ${depLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const cancelSub = async (p) => {
    if (!window.confirm(`Cancel ${p.plan} at the end of the current paid period? The customer keeps access until then and is not charged again.`)) return;
    try {
      await axios.post(`${API}/payments/cancel-subscription`, { session_id: p.session_id },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscription will cancel at the end of the paid period.");
      load(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel. Try again.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <Reveal>
          <form onSubmit={login} data-testid="admin-login-form" className="glass rounded-3xl p-8 w-full max-w-sm">
            <span className="w-12 h-12 rounded-2xl bg-[#0055FF]/15 border border-[#0055FF]/25 flex items-center justify-center mb-5">
              <Lock className="w-5 h-5 text-blue-400" />
            </span>
            <h1 className="font-display text-2xl font-bold text-white tracking-tight">WebStudios Admin</h1>
            <p className="mt-1.5 text-sm text-neutral-500">Enter your admin passcode to continue.</p>
            <input type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)}
              data-testid="admin-passcode-input" placeholder="Passcode" aria-label="Admin passcode"
              className={`${inputCls} mt-6`} autoComplete="current-password" />
            {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={busy || !passcode} data-testid="admin-login-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-colors duration-200">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </button>
            <a href="/" data-testid="admin-back-home" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors duration-200">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to website
            </a>
          </form>
        </Reveal>
      </div>
    );
  }

  const leads = enquiries.filter((x) => x.source === "ai-chat");
  const forms = enquiries.filter((x) => x.source !== "ai-chat");
  const tabs = [
    { id: "enquiries", label: "Enquiries", icon: Inbox, count: forms.length },
    { id: "leads", label: "AI Leads", icon: Flame, count: leads.length },
    { id: "payments", label: "Payments", icon: CreditCard, count: payments.filter((p) => p.payment_status === "paid").length },
    { id: "chats", label: "AI Chats", icon: MessagesSquare, count: chats.length },
  ];
  const list = tab === "leads" ? leads : forms;

  return (
    <div className="admin-dark min-h-screen bg-[#0a0a0a] text-neutral-200">
      <header className="border-b border-white/5 sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-xl z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-[#0055FF] flex items-center justify-center font-display font-black text-white text-sm">W</span>
            <h1 className="font-display font-bold text-white tracking-tight">Enquiries Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site" className="text-xs text-neutral-500 hover:text-white transition-colors duration-200">View site</a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-red-400 transition-colors duration-200">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dashboard">
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-200 border ${
                tab === id ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/10 text-neutral-400 hover:border-white/25 hover:text-white"
              }`}>
              <Icon className="w-4 h-4" /> {label}
              <span className={`text-xs rounded-full px-2 py-0.5 ${tab === id ? "bg-white/20" : "bg-white/5"}`}>{count}</span>
            </button>
          ))}
        </div>

        {tab === "payments" ? (
          <div className="space-y-6">
            <form onSubmit={generateDeposit} className="glass rounded-2xl p-6" data-testid="deposit-link-form">
              <div className="flex items-center gap-2.5 mb-4">
                <Link2 className="w-4 h-4 text-cyan-400" />
                <h2 className="font-display font-bold text-white tracking-tight">Create a deposit payment link</h2>
              </div>
              <p className="text-xs text-neutral-500 mb-5">Custom amount, one-off card payment. WhatsApp the link to your client — you're notified by email the moment they pay.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <input type="number" min="5" step="0.01" value={depAmount} onChange={(e) => setDepAmount(e.target.value)}
                  data-testid="deposit-amount" placeholder="Amount (£), e.g. 250" required className={inputCls} />
                <input value={depDesc} onChange={(e) => setDepDesc(e.target.value)}
                  data-testid="deposit-description" placeholder="What it's for, e.g. Deposit — bakery website" required className={inputCls} />
                <input value={depClient} onChange={(e) => setDepClient(e.target.value)}
                  data-testid="deposit-client" placeholder="Client name (optional)" className={inputCls} />
              </div>
              <button type="submit" disabled={depBusy || !depAmount || !depDesc.trim()} data-testid="deposit-generate"
                className="mt-4 inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">
                {depBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Generate payment link
              </button>
              {depLink && (
                <div className="mt-5 rounded-xl bg-white/[0.04] border border-white/10 p-4" data-testid="deposit-result">
                  <p className="text-xs text-neutral-500 mb-2">Payment link ready:</p>
                  <p className="text-sm text-cyan-300 break-all font-mono2">{depLink}</p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    <button type="button" onClick={copyLink} data-testid="deposit-copy"
                      className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors duration-200">
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied" : "Copy link"}
                    </button>
                    <button type="button" onClick={waShare} data-testid="deposit-whatsapp"
                      className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1fb857] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                      Send via WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </form>

            <div className="space-y-3">
              {payments.length === 0 && <p className="text-neutral-500 text-sm py-12 text-center" data-testid="admin-payments-empty">No transactions yet.</p>}
              {payments.map((p) => {
                const displayStatus = ["cancelling", "cancelled"].includes(p.status) ? p.status : p.payment_status;
                const canCancel = p.recurring && p.stripe_subscription_id && p.payment_status === "paid" && !["cancelling", "cancelled"].includes(p.status);
                return (
                <article key={p.session_id} data-testid={`admin-payment-${p.session_id.slice(-8)}`} className="glass rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-white">{p.plan}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {fmtDate(p.created_at)}
                      {p.customer_email && <> · {p.customer_email}</>}
                      {p.client_name && <> · {p.client_name}</>}
                      {p.recurring ? " · monthly" : " · one-off"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono2 text-base font-semibold text-white">{fmtMoney(p.amount, p.currency)}</span>
                    <span className={`text-[11px] font-medium border rounded-full px-3 py-1 ${STATUS_STYLE[displayStatus] || STATUS_STYLE.pending}`}
                      data-testid={`payment-status-${p.session_id.slice(-8)}`}>
                      {displayStatus === "cancelling" ? "cancels at period end" : displayStatus}
                    </span>
                    {canCancel && (
                      <button onClick={() => cancelSub(p)} data-testid={`cancel-sub-${p.session_id.slice(-8)}`}
                        className="text-[11px] font-medium text-red-400 hover:text-red-300 border border-red-400/25 hover:border-red-400/50 rounded-full px-3 py-1 transition-colors duration-200">
                        Cancel subscription
                      </button>
                    )}
                  </div>
                </article>
                );
              })}
            </div>
          </div>
        ) : tab !== "chats" ? (
          <div className="space-y-3">
            {list.length === 0 && <p className="text-neutral-500 text-sm py-16 text-center" data-testid="admin-empty">Nothing here yet.</p>}
            {list.map((x) => (
              <article key={x.id} data-testid={`admin-enquiry-${x.id}`} className="glass rounded-2xl p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-bold text-white text-lg tracking-tight">{x.name}
                      {x.source === "ai-chat" && <span className="ml-2 text-[10px] font-mono2 uppercase tracking-widest text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-2.5 py-1 align-middle">AI lead</span>}
                    </p>
                    <p className="text-sm text-neutral-400 mt-0.5">
                      <a href={`mailto:${x.email}`} className="text-blue-400 hover:underline">{x.email}</a>
                      {x.phone && <> · {x.phone}</>}
                      {x.business_name && <> · {x.business_name}</>}
                    </p>
                  </div>
                  <span className="text-xs text-neutral-500 font-mono2">{fmtDate(x.created_at)}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {x.service && <span className="text-[11px] text-neutral-300 bg-white/[0.05] border border-white/[0.08] rounded-full px-2.5 py-1">{x.service}</span>}
                  {x.budget && <span className="text-[11px] text-neutral-300 bg-white/[0.05] border border-white/[0.08] rounded-full px-2.5 py-1">{x.budget}</span>}
                  {x.website_url && <span className="text-[11px] text-neutral-400 bg-white/[0.05] border border-white/[0.08] rounded-full px-2.5 py-1">{x.website_url}</span>}
                </div>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed whitespace-pre-wrap">{x.message}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {chats.length === 0 && <p className="text-neutral-500 text-sm py-16 text-center" data-testid="admin-chats-empty">No AI conversations yet.</p>}
            {chats.map((s) => (
              <article key={s.session_id} data-testid={`admin-chat-${s.session_id.slice(0, 8)}`} className="glass rounded-2xl overflow-hidden">
                <button onClick={() => setOpenChat(openChat === s.session_id ? null : s.session_id)}
                  data-testid={`admin-chat-toggle-${s.session_id.slice(0, 8)}`}
                  aria-expanded={openChat === s.session_id}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.02] transition-colors duration-200">
                  <div>
                    <p className="text-sm font-semibold text-white">Session {s.session_id.slice(0, 8)}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{s.messages.length} messages · started {fmtDate(s.started)}</p>
                  </div>
                  <p className="text-xs text-neutral-400 max-w-[50%] truncate hidden sm:block">{s.messages.find((m) => m.role === "visitor")?.content}</p>
                </button>
                {openChat === s.session_id && (
                  <div className="border-t border-white/5 p-5 space-y-2.5 bg-white/[0.01]">
                    {s.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                          m.role === "visitor" ? "bg-[#0055FF] text-white" : "bg-white/[0.06] text-neutral-300"
                        }`}>{m.content}</div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
